/*
 * <cdx-konfigurator> – bundle configurator below the product (sections/cdx-konfigurator.liquid).
 * The Aris/AVIS app renders its option fields into the .avis-anchor-kw inside.
 *
 * - [data-cdx-konfigurator-open] (the "Anpassen…" button in cdx-buy-buttons) shows it, with a loading
 *   state until AVIS has rendered (.avis-input-hiddens, max. 10 s); only then is "Zum Warenkorb" enabled
 * - [data-cdx-konfigurator-close] hides it again
 * - #cdx-config-summary lists the chosen options (label: quantity x value), kept in sync with AVIS
 */
if (!customElements.get("cdx-konfigurator")) {
  const escapeHtml = (value) => {
    const div = document.createElement("div");
    div.textContent = value;
    return div.innerHTML;
  };

  customElements.define(
    "cdx-konfigurator",
    class CdxKonfigurator extends HTMLElement {
      connectedCallback() {
        this.section = this.querySelector("#konfigurator-section");
        this.summary = this.querySelector("#cdx-config-summary");
        if (!this.section) return;

        this.abortController = new AbortController();
        this.addEventListener(
          "click",
          (event) => {
            if (event.target.closest("[data-cdx-konfigurator-close]")) this.close();
          },
          { signal: this.abortController.signal }
        );

        this.whenAvisReady(() => this.startSummary());
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.observers?.forEach((observer) => observer.disconnect());
        clearTimeout(this.loadingTimeout);
      }

      get avisReady() {
        return !!this.querySelector(".avis-input-hiddens");
      }

      // Calls back once AVIS has rendered its fields into this section
      whenAvisReady(callback) {
        if (this.avisReady) {
          callback();
          return null;
        }
        const observer = new MutationObserver(() => {
          if (!this.avisReady) return;
          observer.disconnect();
          callback();
        });
        observer.observe(this, { childList: true, subtree: true });
        (this.observers ||= []).push(observer);
        return observer;
      }

      open(opener) {
        const spinner = opener?.querySelector(".button-overlay-spinner");
        const inlineLoader = this.querySelector("#cdx-avis-loading");
        opener?.classList.add("loading");
        spinner?.classList.remove("hidden");

        const removeLoading = () => {
          clearTimeout(this.loadingTimeout);
          opener?.classList.remove("loading");
          spinner?.classList.add("hidden");
          if (inlineLoader) inlineLoader.style.display = "none";
          const atc = this.querySelector("#cdx-konfig-atc");
          if (atc) atc.disabled = false;
        };

        if (this.avisReady) {
          removeLoading();
        } else {
          if (inlineLoader) inlineLoader.style.display = "block";
          const observer = this.whenAvisReady(removeLoading);
          // Safety net: never leave the spinner running
          this.loadingTimeout = setTimeout(() => {
            observer?.disconnect();
            removeLoading();
          }, 10000);
        }

        this.section.style.display = "block";
        opener?.setAttribute("aria-expanded", "true");
        this.opener = opener;
        setTimeout(() => {
          this.section.scrollIntoView({ behavior: "smooth", block: "start" });
          window.AvisProductOptions?.init?.();
        }, 100);
      }

      close() {
        this.section.style.display = "none";
        this.opener?.setAttribute("aria-expanded", "false");
      }

      // ===== Summary of the chosen AVIS options =====

      startSummary() {
        if (!this.summary) return;
        this.renderSummary();

        const hiddens = this.querySelector(".avis-input-hiddens");
        const observer = new MutationObserver(() => this.renderSummary());
        observer.observe(hiddens, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["name", "disabled", "value"],
        });
        (this.observers ||= []).push(observer);

        // Swatch clicks and dropdown changes update the hidden inputs a moment later
        const avisRoot = this.querySelector("#avpoptions-container__v2");
        if (avisRoot) {
          const { signal } = this.abortController;
          const later = () => setTimeout(() => this.renderSummary(), 300);
          avisRoot.addEventListener("click", later, { signal });
          avisRoot.addEventListener("change", later, { signal });
        }
      }

      // AVIS fieldset for a property name: hidden input inside it, or its <legend> text
      findFieldset(name) {
        const avisRoot = this.querySelector("#avpoptions-container__v2") || this;
        const escaped = CSS.escape(name);
        const hidden = avisRoot.querySelector(
          `fieldset > input[type="hidden"][name="${escaped}"], fieldset > input[type="hidden"][field-name="${escaped}"]`
        );
        if (hidden?.parentElement?.tagName === "FIELDSET") return hidden.parentElement;
        const legend = [...avisRoot.querySelectorAll("fieldset > legend")].find(
          (el) => (el.textContent || "").trim() === name
        );
        return legend ? legend.parentElement : null;
      }

      // Values + quantities from a fieldset (multi-select with quantity, or plain radios/checkboxes)
      readFieldset(fieldset) {
        const grids = fieldset.querySelectorAll(".avp-value-grid");
        if (grids.length) {
          const out = [];
          grids.forEach((grid) => {
            const input = grid.querySelector('input[type="checkbox"], input[type="radio"]');
            if (!input?.checked) return;
            const value = input.getAttribute("data-option-value") || input.value || "";
            const qty = parseInt(grid.querySelector(".avp-qty-input")?.value, 10);
            if (value) out.push({ value: String(value).trim(), qty: qty > 0 ? qty : 1 });
          });
          return out;
        }
        return [...fieldset.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked')]
          .map((input) => input.getAttribute("data-option-value") || input.value || "")
          .filter(Boolean)
          .map((value) => ({ value: String(value).trim(), qty: 1 }));
      }

      readSelections() {
        const hiddens = this.querySelector(".avis-input-hiddens");
        if (!hiddens) return [];
        const items = [];
        hiddens.querySelectorAll('input[type="hidden"]').forEach((input) => {
          if (input.disabled || !input.name) return;
          const name = input.getAttribute("temp-name") || input.name.replace("properties[", "").replace("]", "");
          // Internal AVIS properties start with "_"
          if (!name || name.charAt(0) === "_" || !input.value) return;

          // Prefer the fieldset (handles commas in values and multi-select)
          const fieldset = this.findFieldset(name);
          const fromDom = fieldset ? this.readFieldset(fieldset) : [];
          if (fromDom.length) {
            fromDom.forEach((entry) => items.push({ label: name.trim(), ...entry }));
            return;
          }

          // Single-select fallback: "Foo * 2"
          const [value, qty] = input.value.split(" * ");
          if (value) items.push({ label: name.trim(), value: value.trim(), qty: qty ? parseInt(qty, 10) : 1 });
        });
        return items;
      }

      renderSummary() {
        const items = this.readSelections();
        if (!items.length) {
          this.summary.innerHTML =
            '<div class="cdx-summary-box"><div class="cdx-summary-empty">Noch keine Optionen gewählt</div></div>';
          return;
        }

        // Group by label (multi-select: one label, several values)
        const groups = new Map();
        items.forEach((item) => {
          if (!groups.has(item.label)) groups.set(item.label, []);
          groups.get(item.label).push(item);
        });

        let html = '<div class="cdx-summary-box">';
        groups.forEach((entries, label) => {
          html += `<div class="cdx-summary-group"><div class="cdx-summary-group-label">${escapeHtml(label)}:</div>`;
          entries.forEach((entry) => {
            const qtyLabel = entry.qty > 1 ? `${entry.qty} x ` : "1 x ";
            html += `<div class="cdx-summary-group-value">${qtyLabel}${escapeHtml(entry.value)}</div>`;
          });
          html += "</div>";
        });
        this.summary.innerHTML = `${html}</div>`;
      }
    }
  );

  // The open button sits in the buy buttons, outside the section
  document.addEventListener("click", (event) => {
    const opener = event.target.closest("[data-cdx-konfigurator-open]");
    if (!opener) return;
    document.querySelector("cdx-konfigurator")?.open(opener);
  });
}
